import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MessageCircle,
  Check,
  Clock,
  Banknote,
  FileCheck2,
  Layers,
} from "lucide-react";
import { COUNTRIES, FEE_DISCLAIMER } from "@/data/countries";
import { BRAND, waLink } from "@/data/site";
import { scrollToTop } from "@/lib/lenis";

const BlockTitle = ({ icon: Icon, children }) => (
  <div className="mb-6 flex items-center gap-3">
    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold-400/40 bg-gold-400/10">
      <Icon className="h-4.5 w-4.5 text-gold-300" />
    </span>
    <h2 className="font-serif-display text-2xl text-slate-100 sm:text-3xl">{children}</h2>
  </div>
);

export const DestinationPage = () => {
  const { id } = useParams();
  const c = COUNTRIES[id];

  useEffect(() => {
    scrollToTop();
  }, [id]);

  if (!c) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-navy-950 px-6 text-center">
        <p className="font-mono-tech text-xs tracking-[0.3em] text-gold-400">404</p>
        <h1 className="font-serif-display mt-4 text-3xl text-slate-100">
          Destino não encontrado
        </h1>
        <Link
          data-testid="not-found-home-link"
          to="/"
          className="mt-8 rounded-full border border-gold-400/50 px-6 py-3 text-sm text-gold-200 transition-colors hover:bg-gold-400 hover:text-navy-950"
        >
          Voltar para a página inicial
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="destination-page" className="min-h-screen bg-navy-950">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-gold-400/15 bg-navy-950/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" data-testid="dp-brand-logo" className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Melo Vistos"
              className="h-10 w-10 rounded-md border border-gold-400/40 object-cover"
            />
            <span className="font-mono-tech text-xs font-semibold tracking-[0.28em] text-slate-100">
              MELO <span className="text-gold-400">VISTOS</span>
            </span>
          </Link>
          <a
            data-testid="dp-header-whatsapp"
            href={waLink(`Olá Melo Vistos! Tenho interesse no visto para ${c.country}. Podem me ajudar?`)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-gold-400/50 bg-gold-400/10 px-5 py-2 text-sm font-medium text-gold-200 transition-colors duration-300 hover:bg-gold-400 hover:text-navy-950"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>
      </header>

      <section className="relative flex min-h-[62vh] items-end overflow-hidden">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <img src={c.image} alt={c.country} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 via-navy-950/55 to-navy-950" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_70%,rgba(212,175,55,0.12),transparent_55%)]" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8">
          <Link
            data-testid="dp-back-link"
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-sm font-light text-slate-300 transition-colors hover:text-gold-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Todos os destinos
          </Link>
          <span className="block w-fit rounded-full border border-gold-400/40 bg-navy-950/70 px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.25em] text-gold-200 backdrop-blur-sm">
            {c.tag.toUpperCase()}
            {c.rate ? ` • APROVAÇÃO ${c.rate}` : ""}
          </span>
          <div className="mt-6 overflow-hidden">
            <motion.h1
              data-testid="dp-headline"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              className="font-serif-display max-w-3xl text-4xl font-light leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              {c.country}
              <span className="gold-text block italic">{c.headline}</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.8 }}
            className="mt-6 max-w-2xl text-base font-light leading-relaxed text-slate-300"
          >
            {c.intro}
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-3 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2"
          >
            <BlockTitle icon={Layers}>Modalidades do processo</BlockTitle>
            <div className="mb-14 grid gap-4 sm:grid-cols-3">
              {c.visaTypes.map((v) => (
                <div
                  key={v.name}
                  className="rounded-xl border border-gold-400/20 bg-navy-800/70 p-5"
                >
                  <h3 className="text-sm font-semibold text-gold-200">{v.name}</h3>
                  <p className="mt-2 text-xs font-light leading-relaxed text-slate-400">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>

            <BlockTitle icon={FileCheck2}>Documentos exigidos</BlockTitle>
            <ul data-testid="dp-documents" className="space-y-3.5">
              {c.documents.map((doc) => (
                <li
                  key={doc}
                  className="flex items-start gap-3 border-b border-gold-400/10 pb-3.5 text-sm font-light text-slate-300"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                  {doc}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="space-y-6"
          >
            <div className="rounded-2xl border border-gold-400/25 bg-navy-800/70 p-7">
              <div className="mb-5 flex items-center gap-3">
                <Clock className="h-5 w-5 text-gold-400" />
                <h3 className="font-serif-display text-xl text-slate-100">Prazos estimados</h3>
              </div>
              <ul data-testid="dp-timeline" className="space-y-4">
                {c.prazos.map((p) => (
                  <li key={p.label}>
                    <p className="font-mono-tech text-[10px] tracking-[0.2em] text-slate-500">
                      {p.label.toUpperCase()}
                    </p>
                    <p className="mt-1 text-sm text-slate-200">{p.value}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-gold-400/25 bg-navy-800/70 p-7">
              <div className="mb-5 flex items-center gap-3">
                <Banknote className="h-5 w-5 text-gold-400" />
                <h3 className="font-serif-display text-xl text-slate-100">Taxas & valores</h3>
              </div>
              <ul data-testid="dp-fees" className="space-y-4">
                {c.fees.map((f) => (
                  <li key={f.label} className="flex items-baseline justify-between gap-4">
                    <span className="text-sm font-light text-slate-400">{f.label}</span>
                    <span className="text-right text-sm font-medium text-gold-200">
                      {f.value}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-gold-400/15 pt-4 text-[11px] font-light leading-relaxed text-slate-500">
                {FEE_DISCLAIMER}
              </p>
            </div>

            <a
              data-testid="dp-whatsapp-cta"
              href={waLink(`Olá Melo Vistos! Li a página sobre ${c.country} e quero iniciar meu processo. Podem me passar o orçamento?`)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-full bg-gold-400 px-6 py-4 text-sm font-semibold text-navy-950 transition-colors duration-300 hover:bg-gold-200"
            >
              <MessageCircle className="h-4 w-4" />
              Pedir orçamento — {c.country}
            </a>
            <Link
              data-testid="dp-schedule-link"
              to="/#contato"
              className="flex items-center justify-center gap-2 rounded-full border border-slate-600/60 px-6 py-4 text-sm text-slate-200 transition-colors duration-300 hover:border-gold-400/60 hover:text-gold-200"
            >
              Agendar consultoria online
            </Link>
          </motion.aside>
        </div>
      </section>
    </div>
  );
};

export default DestinationPage;
